// ============================================
// App.js - UPDATED WITH PERMISSION SYSTEM + REGISTRATION MODULE
// ============================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  GraduationCap, LogOut, Menu, X, Package, Bus, Fuel, CreditCard,
  BarChart, Users, Check, AlertCircle, DollarSign,
  FileText, Trophy, Smartphone, Shield, ClipboardList, Baby, Music, Sun, ShoppingBag
} from 'lucide-react';

// Components
import Dashboard from './components/Dashboard';
import Afterschool from './components/Afterschool';
import BusModule from './components/Bus';
import Store from './components/Store';
import Gas from './components/Gas';
import Payments from './components/Payments';
import Reports from './components/Reports';
import UsersModule from './components/Users';
import FamilyBalances from './components/FamilyBalances';
import Ifada from './components/Ifada';
import Activities from './components/Activities';
import MobileAppManager from './components/MobileAppManager';
import PermissionMatrix from './components/PermissionMatrix';
import Registration from './components/Registration';  // ← Registration Module
import Daycare from './components/Daycare';            // ← Daycare Module
import ArtSchool from './components/ArtSchool';        // ← Art School Module
import Colonie from './components/Colonie';            // ← Colonie Summer Camp
import Academic from './components/academic/Academic'; // ← Academic & Student Management

// Permission Context
import { PermissionProvider, usePermissions } from './contexts/PermissionContext';
import { AccessStudioProvider, AccessToolbar, ModuleAccessGate } from './components/AccessStudio';

const API_URL = process.env.REACT_APP_API_URL || 'http://192.168.5.45:3000/api';

// Teacher portal build: a separate public site (portal.apotres.edu.lb) that
// reuses this app but shows ONLY the Academic module (agenda + grades). Built
// with REACT_APP_TEACHER_PORTAL=true and REACT_APP_API_URL=/api. The intranet
// SMS build leaves this unset and behaves exactly as before.
const TEACHER_PORTAL = process.env.REACT_APP_TEACHER_PORTAL === 'true';

// ============================================
// NAVIGATION CONFIG - Define modules with permission mappings
// ============================================
const NAVIGATION_CONFIG = [
  { 
    id: 'dashboard', 
    label: 'Dashboard', 
    icon: BarChart,
    module: 'dashboard',
    feature: 'summary'
  },
  // ============================================
  // REGISTRATION MODULE
  // ============================================
  {
    id: 'registration',
    label: 'Registration 26-27',
    icon: ClipboardList,
    module: 'registration',
    feature: 'view_registrations',
    // any of these grants should also surface the tab
    alternateFeatures: ['view_reports', 'configure_pricing', 're_registration', 'create_registration', 'manage_children', 'manage_payment_plan']
  },
  {
    id: 'academic',
    label: 'Academic',
    icon: GraduationCap,
    module: 'academic',
    feature: 'roster',
    alternateFeatures: ['attendance', 'behavior', 'grading', 'reports']
  },
  // ============================================
  { 
    id: 'afterschool',
    label: 'CAP',
    icon: Package,
    module: 'afterschool',
    feature: 'enrollments'
  },
  { 
    id: 'activities', 
    label: 'Activities', 
    icon: Trophy,
    module: 'activities',
    feature: 'enrollments',
    alternateFeatures: ['manage_activities', 'reports']
  },
  { 
    id: 'daycare', 
    label: 'Daycare', 
    icon: Baby,
    module: 'daycare',
    feature: 'packages',
    alternateFeatures: ['children', 'enrollments', 'billing', 'reports', 'daily_report', 'classes', 'attendance']
  },
  // ============================================
  // ART SCHOOL MODULE
  // ============================================
  { 
    id: 'artschool', 
    label: 'Art School', 
    icon: Music,
    module: 'artschool',
    feature: 'enrollments'
  },
  // ============================================
  // COLONIE SUMMER CAMP MODULE
  // ============================================
  { 
    id: 'colonie', 
    label: 'Colonie Des Rêves', 
    icon: Sun,
    module: 'colonie',
    feature: 'view',
    allowedRoles: ['GlobalAdmin','Cashier']
  },
  // ============================================
  { 
    id: 'bus', 
    label: 'Bus', 
    icon: Bus,
    module: 'bus',
    feature: 'student_enrollments',
    alternateFeatures: ['group_rentals']
  },
  {
    id: 'store',
    label: 'Store',
    icon: ShoppingBag,
    module: 'store',
    feature: 'orders',
    alternateFeatures: ['catalog', 'payments']
  },
  {
    id: 'gas',
    label: 'Gas Station',
    icon: Fuel,
    module: 'gas',
    feature: 'fuel_logs',
    alternateFeatures: ['pricing', 'reports']
  },
  { 
    id: 'payments', 
    label: 'Payments', 
    icon: CreditCard,
    module: 'payments',
    feature: 'usd_payments'
  },
  { 
    id: 'ifada', 
    label: 'Ifada / إفادات', 
    icon: FileText,
    module: 'ifada',
    feature: 'orders'
  },
  { 
    id: 'family-balances', 
    label: 'Family Balances', 
    icon: DollarSign,
    module: 'family_balances',
    feature: 'view_balances'
  },
  { 
    id: 'mobile-app', 
    label: 'Mobile App', 
    icon: Smartphone,
    module: 'mobile_app',
    feature: 'send_message'
  },
  { 
    id: 'reports', 
    label: 'Reports', 
    icon: BarChart,
    module: 'reports',
    feature: 'outstanding_balances'
  },
  { 
    id: 'users', 
    label: 'Users', 
    icon: Users,
    module: 'users',
    feature: 'user_list'
  },
  { 
    id: 'permissions', 
    label: 'Permissions', 
    icon: Shield,
    module: 'users',
    feature: 'manage_roles',
    adminOnly: true
  }
];

// In the teacher portal build, expose only the Academic tab (agenda + grades).
const VISIBLE_NAV = TEACHER_PORTAL
  ? NAVIGATION_CONFIG.filter(item => item.id === 'academic')
  : NAVIGATION_CONFIG;

// ONE MSAL instance for the whole session, initialized once. Creating a new
// instance per click, or skipping handleRedirectPromise, causes MSAL's
// "interaction_in_progress" error when a prior popup was interrupted.
let _msalInit = null;
function getMsalInstance() {
  if (!_msalInit) {
    _msalInit = (async () => {
      const { PublicClientApplication } = await import('@azure/msal-browser');
      const pca = new PublicClientApplication({
        auth: {
          clientId: process.env.REACT_APP_AAD_CLIENT_ID,
          authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AAD_TENANT_ID}`,
          // Redirect flow: Microsoft sends us back to the app itself, where
          // completeSsoRedirect() (on mount) reads the code and finishes sign-in.
          redirectUri: window.location.origin,
        },
        cache: { cacheLocation: 'sessionStorage' },
      });
      await pca.initialize();
      return pca;
    })();
  }
  return _msalInit;
}

// ============================================
// SIDEBAR NAVIGATION COMPONENT
// ============================================
const SidebarNavigation = ({ currentModule, setCurrentModule, sidebarOpen, user }) => {
  const { hasPermission, isGlobalAdmin } = usePermissions();

  return (
    <nav className="space-y-2">
      {VISIBLE_NAV.map(item => {
        // Check if user can access this navigation item
        let canAccess = false;
        
        if (item.adminOnly) {
          canAccess = isGlobalAdmin;
        } else if (item.allowedRoles) {
          // Module uses role-based access instead of granular permissions
          canAccess = isGlobalAdmin || item.allowedRoles.some(role => 
            user?.roles?.includes(role)
          );
        } else {
          // Check primary feature
          canAccess = hasPermission(item.module, item.feature, 'view');
          
          // Also check alternate features if defined
          if (!canAccess && item.alternateFeatures) {
            canAccess = item.alternateFeatures.some(feature => 
              hasPermission(item.module, feature, 'view') || 
              hasPermission(item.module, feature, 'print') ||
              hasPermission(item.module, feature, 'create') ||
              hasPermission(item.module, feature, 'edit')
            );
          }
        }

        if (!canAccess) return null;

        const Icon = item.icon;
        
        return (
          <button 
            key={item.id}
            onClick={() => setCurrentModule(item.id)} 
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
              currentModule === item.id ? 'bg-blue-700' : 'hover:bg-blue-700'
            }`}
          >
            <Icon size={20} />
            {sidebarOpen && <span>{item.label}</span>}
          </button>
        );
      })}
    </nav>
  );
};

// ============================================
// MAIN APP CONTENT (Inner component that uses permissions)
// ============================================
const AppContent = ({
  user,
  token,
  apiCall,
  selectedYear,
  setSelectedYear,
  availableYears = [],
  currentAcademicYear,
  currentTrimester,
  showSuccess,
  showError
}) => {
  const [currentModule, setCurrentModule] = useState(TEACHER_PORTAL ? 'academic' : 'dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { canAccessModule, hasPermission, roles, isGlobalAdmin } = usePermissions();

  // Pure teacher/surveillant/principal accounts are pinned to the active
  // academic year — no year switcher, current year only. A user who ALSO holds
  // any other role (Afterschool, Registration, Ifada, Cashier, Accounting…)
  // keeps the switcher, since those need past/other years for financial work.
  // Admins always keep full year access.
  const YEAR_LOCKED_ROLES = ['Teacher', 'Surveillant', 'Principal'];
  const lockYear = !isGlobalAdmin && roles.length > 0 && roles.every(r => YEAR_LOCKED_ROLES.includes(r));

  // A selected year is "past" (older than the active year → mostly read-only)
  // or "upcoming" (a future year being prepared, e.g. 26-27 before the flip).
  // Plain string compare works for the 'YY-YY' format.
  const isPastYear = !!(currentAcademicYear && selectedYear < currentAcademicYear);
  const isFutureYear = !!(currentAcademicYear && selectedYear > currentAcademicYear);

  // Set initial module based on permissions
  useEffect(() => {
    if (TEACHER_PORTAL) { setCurrentModule('academic'); return; }
    if (!canAccessModule('dashboard')) {
      const firstAccessible = NAVIGATION_CONFIG.find(item =>
        canAccessModule(item.module)
      );
      if (firstAccessible) {
        setCurrentModule(firstAccessible.id);
      }
    }
  }, [canAccessModule]);

  const moduleTitle = {
    dashboard: 'Dashboard',
    registration: 'Student Registration 2026-27',
    academic: 'Academic & Student Management',
    afterschool: 'CAP — Centre d’Accompagnement Pédagogique',
    activities: 'Activities',
    daycare: 'Daycare Management',
    artschool: 'Art School',                          // ← NEW
    colonie: '🌞 Colonie Des Rêves — Summer Camp',
    bus: 'Bus Management',
    gas: 'Gas Station',
    payments: 'Payments',
    'family-balances': 'Family Balances',
    ifada: 'Ifada / إفادات وشهادات',
    'mobile-app': 'Mobile App Manager',
    reports: 'Reports & Analytics',
    users: 'User Management',
    permissions: 'Permission Management'
  };

  // Render current module content
  const renderModule = () => {
    switch (currentModule) {
      case 'dashboard':
        return <Dashboard apiCall={apiCall} selectedYear={selectedYear} hasRole={() => true} />;
      
      // ============================================
      // REGISTRATION MODULE
      // ============================================
      case 'registration':
        return (
          <Registration
            apiCall={apiCall}
            selectedYear={selectedYear}
            hasRole={(role) => user.roles?.includes(role)}
            hasPermission={hasPermission}
            showSuccess={showSuccess}
            showError={showError}
          />
        );

      // ============================================
      // ACADEMIC & STUDENT MANAGEMENT MODULE
      // ============================================
      case 'academic':
        return (
          <Academic
            apiCall={apiCall}
            selectedYear={selectedYear}
            hasPermission={hasPermission}
            showSuccess={showSuccess}
            showError={showError}
          />
        );
      // ============================================
      
      case 'afterschool':
        return (
          <Afterschool 
            apiCall={apiCall} 
            selectedYear={selectedYear} 
            hasRole={(role) => user.roles?.includes(role)}
            showSuccess={showSuccess} 
            showError={showError} 
          />
        );
      
      case 'activities':
        return (
          <Activities 
            apiCall={apiCall} 
            selectedYear={selectedYear} 
            hasRole={(role) => user.roles?.includes(role)}
            hasPermission={hasPermission}
            showSuccess={showSuccess} 
            showError={showError} 
          />
        );
      
      case 'daycare':
        return (
          <Daycare
            apiCall={apiCall}
            // Daycare is continuous — it has its OWN year selector (independent
            // of the global switcher). Seed it with the active year.
            selectedYear={currentAcademicYear || selectedYear}
            availableYears={availableYears}
            hasRole={(role) => user.roles?.includes(role)}
            showSuccess={showSuccess}
            showError={showError}
          />
        );

      // ============================================
      // ART SCHOOL MODULE
      // ============================================
      case 'artschool':
        return (
          <ArtSchool
            apiCall={apiCall}
            // Art School is continuous like Daycare — ignores the global year
            // switcher and always works in the active academic year.
            selectedYear={currentAcademicYear || selectedYear}
            hasRole={(role) => user.roles?.includes(role)}
            showSuccess={showSuccess}
            showError={showError}
          />
        );
      // ============================================

      // ============================================
      // COLONIE SUMMER CAMP MODULE
      // ============================================
      case 'colonie':
        return (
          <Colonie
            apiCall={apiCall}
            hasRole={(role) => user.roles?.includes(role)}
            showSuccess={showSuccess}
            showError={showError}
          />
        );
      // ============================================
      
      case 'bus':
        return (
          <BusModule 
            apiCall={apiCall} 
            selectedYear={selectedYear} 
            currentTrimester={currentTrimester}
            hasRole={(...roles) => roles.some(role => user.roles?.includes(role))}
            hasPermission={hasPermission}
            showSuccess={showSuccess} 
            showError={showError} 
          />
        );
      
      case 'store':
        return (
          <Store
            apiCall={apiCall}
            selectedYear={selectedYear}
            hasRole={(...roles) => roles.some(role => user.roles?.includes(role))}
            hasPermission={hasPermission}
            showSuccess={showSuccess}
            showError={showError}
          />
        );

      case 'gas':
        return (
          <Gas 
            apiCall={apiCall} 
            hasRole={(role) => user.roles?.includes(role)}
            showSuccess={showSuccess} 
            showError={showError} 
          />
        );
      
      case 'payments':
        return (
          <Payments
            apiCall={apiCall}
            selectedYear={selectedYear}
            hasRole={(role) => user.roles?.includes(role)}
            showSuccess={showSuccess}
            showError={showError}
          />
        );
      
      case 'ifada':
        return (
          <Ifada
            apiCall={apiCall}
            selectedYear={selectedYear}
            availableYears={availableYears}
            hasRole={(role) => user.roles?.includes(role)}
            showSuccess={showSuccess}
            showError={showError}
          />
        );
      
      case 'family-balances':
        return (
          <FamilyBalances
            apiCall={apiCall}
            selectedYear={selectedYear}
            currentAcademicYear={currentAcademicYear}
            hasRole={(role) => user.roles?.includes(role)}
            showSuccess={showSuccess}
            showError={showError}
          />
        );
      
      case 'mobile-app':
        return (
          <MobileAppManager 
            apiCall={apiCall} 
            showSuccess={showSuccess} 
            showError={showError} 
          />
        );
      
      case 'reports':
        return (
          <Reports 
            apiCall={apiCall} 
            selectedYear={selectedYear} 
            hasRole={(role) => user.roles?.includes(role)}
            showSuccess={showSuccess} 
            showError={showError} 
          />
        );
      
      case 'users':
        return (
          <UsersModule 
            apiCall={apiCall} 
            showSuccess={showSuccess} 
            showError={showError} 
          />
        );
      
      case 'permissions':
        return (
          <PermissionMatrix 
            apiCall={apiCall} 
            showSuccess={showSuccess} 
            showError={showError} 
          />
        );
      
      default:
        return <div className="text-center py-8">Module not found</div>;
    }
  };

  return (
    <AccessStudioProvider apiCall={apiCall} user={user} userHasPermission={hasPermission} pastYear={isPastYear} enabled={!TEACHER_PORTAL}>
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar — hidden in the teacher portal (only the Academic section) so
          the content is full-width on laptops and phones. */}
      {!TEACHER_PORTAL && (
      <div className={`bg-gradient-to-b from-blue-900 to-blue-800 text-white transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-20'} relative`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-8">
            {sidebarOpen && <h2 className="text-xl font-bold">SMS v2.0</h2>}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-blue-700 rounded-lg">
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
          
          {/* Dynamic Navigation */}
          <SidebarNavigation 
            currentModule={currentModule}
            setCurrentModule={setCurrentModule}
            sidebarOpen={sidebarOpen}
            user={user}
          />
        </div>

        {/* User Info & Logout */}
        <div className="absolute bottom-4 left-4 right-4">
          {sidebarOpen && (
            <div className="mb-2 px-3 py-2 bg-blue-800 rounded-lg text-sm">
              <div className="font-semibold">{user.fullName}</div>
              <div className="text-blue-300 text-xs">
                {user.roles?.join(', ')}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-white shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{moduleTitle[currentModule]}</h1>
              <p className="text-gray-600">Welcome, {user.fullName}</p>
            </div>

            {/* Global academic-year switcher — defaults to the active year;
                switch to a past year to view historical archives. Hidden for
                year-locked roles (teachers / surveillants / principals). */}
            {setSelectedYear && !lockYear && (
              <div className="flex items-center gap-3">
                {isPastYear && (
                  <span className="text-xs text-amber-600 font-medium">
                    Past year · read-only for most actions
                  </span>
                )}
                {isFutureYear && (
                  <span className="text-xs text-blue-600 font-medium">
                    Upcoming year
                  </span>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Year</span>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className={`border rounded-lg px-3 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isPastYear ? 'border-amber-400 bg-amber-50 text-amber-800'
                        : isFutureYear ? 'border-blue-400 bg-blue-50 text-blue-800'
                        : 'border-gray-300 text-gray-800 bg-white'
                    }`}
                  >
                    {[...new Set([currentAcademicYear, ...availableYears, selectedYear].filter(Boolean))].map((y) => (
                      <option key={y} value={y}>{y}{currentAcademicYear === y ? ' (current)' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Module Content */}
        <div className="p-6">
          <ModuleAccessGate navConfig={NAVIGATION_CONFIG} currentModule={currentModule} user={user}>
            {renderModule()}
          </ModuleAccessGate>
        </div>
      </div>
    </div>
    <AccessToolbar />
    </AccessStudioProvider>
  );
};

// ============================================
// MAIN APP COMPONENT
// ============================================
const App = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [currentAcademicYear, setCurrentAcademicYear] = useState('25-26');
  // Global year context: defaults to the active year (SystemSettings.CurrentAcademicYear
  // via /system/current-period) once loaded; users can switch to view past years.
  const [selectedYear, setSelectedYear] = useState('25-26');
  const [availableYears, setAvailableYears] = useState(['25-26', '24-25']);
  const [currentTrimester, setCurrentTrimester] = useState(1);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // API Call helper
  const apiCall = useCallback(async (endpoint, options = {}) => {
    const { raw, ...fetchOptions } = options;
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...fetchOptions,
      headers: { ...headers, ...fetchOptions.headers }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'API Error');
    }
    // raw: return the Response itself (for binary downloads like scanned files)
    return raw ? response : response.json();
  }, [token]);

  const showSuccess = useCallback((msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  }, []);

  const showError = useCallback((msg) => {
    setError(msg);
    setTimeout(() => setError(''), 3000);
  }, []);

  const handleLogout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }, []);

  const handleLogin = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify(loginForm)
      });
      
      setUser(data.user);
      setToken(data.token);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      console.log('Login successful, permissions:', data.user.permissions);
    } catch (error) {
      showError('Login failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Office 365 (Entra ID) single sign-on — teacher portal only. Gets a Microsoft
  // ID token via MSAL, hands it to the teacher API (/auth/sso), which verifies it
  // and maps the email to a Users row, returning our own app JWT. MSAL is loaded
  // lazily so the intranet build never touches it at runtime.
  const handleSsoLogin = async () => {
    try {
      if (!process.env.REACT_APP_AAD_CLIENT_ID || !process.env.REACT_APP_AAD_TENANT_ID) {
        showError('Microsoft sign-in is not configured.'); return;
      }
      setLoading(true);
      const pca = await getMsalInstance();
      const scopes = ['openid', 'profile', 'email', 'User.Read'];
      // Redirect flow (not popup): the whole page navigates to Microsoft and
      // returns to the app, where completeSsoRedirect() finishes the sign-in.
      // Avoids the popup opener-handle / COOP issues that leave a blank popup.
      try {
        await pca.loginRedirect({ scopes });
      } catch (e) {
        // A prior interrupted interaction can block a new one — clear and retry.
        if (e && e.errorCode === 'interaction_in_progress') {
          try { Object.keys(sessionStorage).forEach(k => { if (k.startsWith('msal.')) sessionStorage.removeItem(k); }); } catch { /* ignore */ }
          await pca.loginRedirect({ scopes });
        } else { throw e; }
      }
      // Execution does not continue past loginRedirect — the page navigates away.
    } catch (error) {
      showError('Microsoft sign-in failed: ' + (error.message || 'unknown error'));
      setLoading(false);
    }
  };

  // Complete an Office 365 redirect sign-in. When Microsoft sends the browser
  // back to the app, MSAL hands us the ID token here; we exchange it with the
  // teacher API (/auth/sso) for our own app JWT. No-op unless we just returned
  // from a redirect. Teacher portal only; MSAL is loaded lazily.
  const completeSsoRedirect = useCallback(async () => {
    if (!TEACHER_PORTAL) return;
    if (!process.env.REACT_APP_AAD_CLIENT_ID || !process.env.REACT_APP_AAD_TENANT_ID) return;
    try {
      const pca = await getMsalInstance();
      const result = await pca.handleRedirectPromise();
      if (result && result.idToken) {
        setLoading(true);
        const data = await apiCall('/auth/sso', {
          method: 'POST',
          body: JSON.stringify({ idToken: result.idToken }),
        });
        setUser(data.user);
        setToken(data.token);
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
      }
    } catch (error) {
      showError('Microsoft sign-in failed: ' + (error.message || 'unknown error'));
    } finally {
      setLoading(false);
    }
  }, [apiCall, showError]);

  const loadSystemData = useCallback(async () => {
    try {
      const period = await apiCall('/system/current-period');
      setCurrentAcademicYear(period.AcademicYear);
      setCurrentTrimester(period.Trimester);
      // Default the whole app to the active year on login (the flip to 26-27 in
      // SystemSettings is what makes this default land on the new year).
      setSelectedYear(period.AcademicYear);

      const years = await apiCall('/system/academic-years');
      setAvailableYears(years.map(y => y.AcademicYear));
    } catch (error) {
      if (error.message.includes('Invalid token') || error.message.includes('Access denied')) {
        showError('Session expired. Please login again.');
        handleLogout();
      } else {
        showError('Error loading data: ' + error.message);
      }
    }
  }, [apiCall, showError, handleLogout]);

  // Restore session on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }
  }, []);

  // Finish an Office 365 redirect sign-in if we just came back from Microsoft.
  useEffect(() => { completeSsoRedirect(); }, [completeSsoRedirect]);

  // Load system data when logged in
  useEffect(() => {
    if (token) {
      loadSystemData();
    }
  }, [token, loadSystemData]);

  // Login Screen
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center p-4">
        {error && (
          <div className="fixed top-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50">
            <AlertCircle size={20} className="shrink-0" />
            <span className="min-w-0 break-words">{error}</span>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="text-blue-600" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">{TEACHER_PORTAL ? 'Teacher Portal' : 'School Management'}</h1>
            <p className="text-gray-600">Sign in to continue</p>
          </div>

          <div className="space-y-4">
            {TEACHER_PORTAL ? (
              // Teacher portal: Office 365 SSO only (no username/password).
              <>
                <button
                  onClick={handleSsoLogin}
                  disabled={loading}
                  className="w-full bg-[#2f2f2f] hover:bg-black text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? 'Signing in…' : 'Sign in with Office 365'}
                </button>
                <p className="text-xs text-gray-500 text-center">Use your school Microsoft account.</p>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    type="text"
                    autoComplete="username"
                    value={loginForm.username}
                    onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter username"
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter password"
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  />
                </div>
                <button
                  onClick={handleLogin}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main App - Wrapped in PermissionProvider
  return (
    <PermissionProvider user={user}>
      {/* Global Notifications */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <AlertCircle size={20} />
          {error}
        </div>
      )}
      {success && (
        <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <Check size={20} />
          {success}
        </div>
      )}

      <AppContent
        user={user}
        token={token}
        apiCall={apiCall}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        availableYears={availableYears}
        currentAcademicYear={currentAcademicYear}
        currentTrimester={currentTrimester}
        showSuccess={showSuccess}
        showError={showError}
      />

      {/* Logout Button - Fixed Position */}
      <button 
        onClick={handleLogout}
        className="fixed bottom-4 left-4 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full shadow-lg z-50"
        title="Logout"
      >
        <LogOut size={20} />
      </button>
    </PermissionProvider>
  );
};

export default App;
